import React, { useState } from 'react';

const ProfileAvatar = ({ 
  name, 
  headshot, 
  size = 'md',
  className = '' 
}) => {
  const [imageError, setImageError] = useState(false);
  
  // Extract initials from name
  const getInitials = (fullName) => {
    if (!fullName) return '?';
    
    const names = fullName.trim().split(' ').filter(n => n.length > 0);
    if (names.length === 0) return '?';
    
    if (names.length === 1) {
      return names[0].charAt(0).toUpperCase();
    }
    
    // Get first and last name initials
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };
  
  // Size variants
  const sizeClasses = {
    sm: 'h-8 w-8 text-sm',
    md: 'h-10 w-10 text-base',
    lg: 'h-16 w-16 text-xl',
    xl: 'h-20 w-20 text-2xl'
  };
  
  // Construct full S3 URL if headshot is just a path
  const getHeadshotUrl = (headshotPath) => {
    if (!headshotPath || headshotPath === '0' || headshotPath === 'null') {
      return null;
    }
    
    // If it's already a full URL, return as is
    if (headshotPath.startsWith('http://') || headshotPath.startsWith('https://')) {
      return headshotPath;
    }
    
    // Otherwise, construct S3 URL
    const S3_BASE_URL = 'https://scholars-uploads.s3.us-east-2.amazonaws.com';
    
    // Handle cases where the path might already include 'headshots/' or not
    if (headshotPath.startsWith('headshots/')) {
      return `${S3_BASE_URL}/${headshotPath}`;
    } else {
      return `${S3_BASE_URL}/headshots/${headshotPath}`;
    }
  };
  
  const headshotUrl = getHeadshotUrl(headshot);
  const hasValidHeadshot = headshotUrl && !imageError;
  
  // Generate a color based on the name (for consistent colors)
  const generateColorFromName = (name) => {
    if (!name) return 'bg-gray-500';
    
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-indigo-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-teal-500'
    ];
    
    // Simple hash function to get consistent color
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };
  
  if (hasValidHeadshot) {
    return (
      <img
        src={headshotUrl}
        alt={`${name}'s profile`}
        className={`${sizeClasses[size]} rounded-full object-cover border-2 border-gray-200 ${className}`}
        onError={() => setImageError(true)}
      />
    );
  }
  
  // Fallback to initials
  const backgroundColor = generateColorFromName(name);
  
  return (
    <div
      className={`${sizeClasses[size]} rounded-full flex items-center justify-center font-semibold text-white ${backgroundColor} ${className}`}
      title={name}
    >
      {getInitials(name)}
    </div>
  );
};

export default ProfileAvatar;